import React, { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

/**
 * Confirmation dialog for destructive item deletion. Requires a non-empty reason
 * before enabling the "Excluir" button. Reason is persisted in the budget
 * history (backend saves it under `deleted_items` and emits a history entry).
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open: boolean) => void
 *  - item: { description, quantity, unit_value, total }
 *  - onConfirm: (reason: string) => Promise<void> | void
 */
export default function DeleteItemDialog({ open, onOpenChange, item, onConfirm, reasonRequired = true }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setBusy(false);
    }
  }, [open]);

  if (!item) return null;

  const canSubmit = (!reasonRequired || reason.trim().length >= 3) && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-item-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Excluir este item?
          </DialogTitle>
          <DialogDescription>
            Esta ação remove o item do orçamento. Para auditoria, o item fica
            registrado no histórico (com motivo e autor) e pode ser consultado
            depois.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
          <p className="font-semibold text-slate-900 break-words">{item.description || "(sem descrição)"}</p>
          <p className="text-xs text-slate-500 mt-1">
            Qtd {item.quantity ?? 1} × {Number(item.unit_value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} ={" "}
            <strong>
              {Number(item.total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            </strong>
          </p>
        </div>

        <div className="mt-3">
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Motivo da exclusão {reasonRequired ? <span className="text-red-500">*</span> : <span className="text-slate-400 font-normal">(opcional antes da aprovação)</span>}
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: item duplicado, incluído por engano, substituído pelo item X, etc."
            rows={3}
            data-testid="delete-item-reason"
            className="resize-none"
            autoFocus
          />
          <p className="text-[11px] text-slate-500 mt-1">Mínimo 3 caracteres.</p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="btn-cancel-delete-item"
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="btn-confirm-delete-item"
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
          >
            {busy ? "Excluindo..." : "Excluir item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
